// ---------------------------------------------------------------------------
// Funder portal service layer.
//
// For now these getters simply return the frontend demo data (see
// constants/funderDemoData.js) — there are NO backend calls and nothing is
// persisted. Each getter is shaped so it can later be swapped for a
// `request("GET", ...)` call from HttpService.js without changing the pages.
// ---------------------------------------------------------------------------
import {
    CALLS, DEFAULT_CALL_ID, PROPOSALS, CALL_OVERVIEW,
    REVIEWER_ROWS, REVIEWER_PIPELINE, COMMITTEE, CLUSTERS, REVIEWERS,
    PREPRINTS, WHITE_SPACE, PORTFOLIO, DECISIONS,
} from "../constants/funderDemoData";

// localStorage key for the currently-selected call (persists across portal nav).
const FT_FUNDER_CALL = "ftFunderCall";

export function getCalls() {
    return CALLS;
}

export function getCall(callId) {
    return CALLS.find((c) => c.id === callId) || CALLS.find((c) => c.id === DEFAULT_CALL_ID) || CALLS[0];
}

// Selected-call helpers (no React Context — read on mount, written by the switcher).
export function getActiveCallId() {
    const stored = localStorage.getItem(FT_FUNDER_CALL);
    if (stored && CALLS.some((c) => c.id === stored)) return stored;
    return DEFAULT_CALL_ID;
}

export function setActiveCallId(callId) {
    if (CALLS.some((c) => c.id === callId)) {
        localStorage.setItem(FT_FUNDER_CALL, callId);
    }
}

export function getClusters(callId) {
    return CLUSTERS[callId] || [];
}

export function getReviewerNames(callId) {
    return REVIEWERS[callId] || [];
}

export function getProposals(callId) {
    return PROPOSALS[callId] || [];
}

export function getProposal(callId, id) {
    const list = PROPOSALS[callId] || [];
    return list.find((p) => String(p.id) === String(id)) || null;
}

export function getOverview(callId) {
    return CALL_OVERVIEW[callId] || null;
}

export function getReviewerRows(callId) {
    return REVIEWER_ROWS[callId] || [];
}

export function getReviewerPipeline(callId) {
    return REVIEWER_PIPELINE[callId] || [];
}

export function getCommittee(callId) {
    return COMMITTEE[callId] || null;
}

export function getPreprints(callId) {
    return PREPRINTS[callId] || [];
}

export function getWhiteSpace(callId) {
    return WHITE_SPACE[callId] || [];
}

export function getPortfolio(callId) {
    return PORTFOLIO[callId] || null;
}

// Pool of proposals to compare on the Funder Comparison page.
export function getComparisonPool(callId) {
    return PROPOSALS[callId] || [];
}

// Decision list derived from the committee shortlist tiers, joined to the
// per-grant feedback status. Returns { decisions, summary, policyNote }.
export function getDecisions(callId) {
    const meta = DECISIONS[callId] || { summary: {}, policyNote: "", feedback: {} };
    const committee = COMMITTEE[callId];
    if (!committee || !committee.shortlist) {
        return { decisions: [], summary: meta.summary, policyNote: meta.policyNote };
    }
    const tag = (rows, decision) =>
        (rows || []).map((r) => ({ ...r, decision, feedback: meta.feedback[r.grantNumber] || { status: "Pending" } }));
    const decisions = [
        ...tag(committee.shortlist.shortlist, "Fund"),
        ...tag(committee.shortlist.discuss, "Hold"),
        ...tag(committee.shortlist.reject, "Decline"),
    ];
    return { decisions, summary: meta.summary, policyNote: meta.policyNote };
}
